import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { FileText } from 'lucide-react';
import type { ChPost, ChPostStatus } from '../types';
import { POST_STATUS_LABELS, PLATFORM_LABELS } from '../types';
import { getCoverUrl, getPlatforms } from '../lib/posts';
import { MediaPreview } from './MediaPreview';
import { DeadlineBadge } from './DeadlineBadge';

function PlatformChips({ post }: { post: ChPost }) {
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1.5">
      {getPlatforms(post).map(p => (
        <span key={p} className="ch-chip text-[10px]">{PLATFORM_LABELS[p]}</span>
      ))}
      <DeadlineBadge post={post} />
    </div>
  );
}

const STATUS_ORDER: ChPostStatus[] = [
  'ideia',
  'em_producao',
  'pronto',
  'em_revisao',
  'aprovado',
  'publicado',
];

interface Props {
  posts: ChPost[];
  onPostClick: (post: ChPost) => void;
  onStatusChange: (postId: string, newStatus: ChPostStatus) => void;
}

function KanbanColumn({ status, children, count }: { status: ChPostStatus; children: React.ReactNode; count: number }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-[260px] md:w-auto md:flex-1 flex flex-col rounded-2xl transition-colors ${
        isOver ? 'bg-[var(--ch-lime)]/10' : 'bg-gray-50'
      }`}
    >
      <div className="px-3 py-2.5 flex items-center gap-2">
        <span className={`ch-badge ch-badge-${status}`}>
          {POST_STATUS_LABELS[status]}
        </span>
        <span className="text-xs text-gray-400 font-medium">{count}</span>
      </div>
      <div className="flex-1 px-2 pb-2 space-y-2 min-h-[120px]">
        {children}
      </div>
    </div>
  );
}

function KanbanCard({ post, onClick }: { post: ChPost; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: post.id,
  });

  const style = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)` }
    : undefined;
  const cover = getCoverUrl(post);

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={style}
      onClick={onClick}
      className={`bg-white rounded-xl p-3 cursor-grab active:cursor-grabbing transition-shadow hover:shadow-md ${
        isDragging ? 'opacity-50 shadow-lg' : ''
      }`}
    >
      {cover && (
        <div className="w-full h-20 rounded-lg overflow-hidden mb-2 bg-gray-100">
          <MediaPreview url={cover} className="w-full h-full object-cover" />
        </div>
      )}
      <p className="text-xs font-semibold text-[var(--ch-ink)] truncate">{post.title}</p>
      <PlatformChips post={post} />
    </div>
  );
}

function DragOverlayCard({ post }: { post: ChPost }) {
  return (
    <div className="bg-white rounded-xl p-3 shadow-xl border border-[var(--ch-lime)]">
      <p className="text-xs font-semibold text-[var(--ch-ink)] truncate">{post.title}</p>
      <PlatformChips post={post} />
    </div>
  );
}

export function PostKanban({ posts, onPostClick, onStatusChange }: Props) {
  const [activePost, setActivePost] = useState<ChPost | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  function handleDragStart(event: DragStartEvent) {
    const post = posts.find(p => p.id === event.active.id);
    setActivePost(post || null);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActivePost(null);
    const { active, over } = event;
    if (!over) return;

    const postId = active.id as string;
    const newStatus = over.id as ChPostStatus;
    const post = posts.find(p => p.id === postId);

    if (post && post.status !== newStatus) {
      onStatusChange(postId, newStatus);
    }
  }

  if (posts.length === 0) {
    return (
      <div className="ch-card">
        <div className="ch-empty">
          <FileText className="ch-empty-icon" />
          <p className="text-gray-500 font-medium">Sem posts</p>
          <p className="text-sm text-gray-400 mt-1">Cria o primeiro post deste projeto.</p>
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0">
        {STATUS_ORDER.map(status => {
          const columnPosts = posts.filter(p => p.status === status);
          return (
            <KanbanColumn key={status} status={status} count={columnPosts.length}>
              {columnPosts.map(post => (
                <KanbanCard
                  key={post.id}
                  post={post}
                  onClick={() => onPostClick(post)}
                />
              ))}
            </KanbanColumn>
          );
        })}
      </div>

      <DragOverlay>
        {activePost ? <DragOverlayCard post={activePost} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
